import clsx from "clsx";
import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary" | "danger" | "ghost" | "outline";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  children: ReactNode;
}

const variantClasses: Record<Variant, string> = {
  primary:
    "bg-accent-blue text-white hover:bg-accent-blue/90 focus-visible:ring-accent-blue",
  secondary:
    "bg-bg-secondary text-ink-primary border border-slate-200 hover:bg-slate-50 focus-visible:ring-slate-500",
  danger: "bg-accent-red text-white hover:bg-accent-red/90 focus-visible:ring-accent-red",
  ghost:
    "bg-transparent text-ink-secondary hover:bg-slate-100 focus-visible:ring-slate-500",
  outline:
    "bg-transparent text-accent-blue border border-accent-blue hover:bg-accent-blue/5 focus-visible:ring-accent-blue",
};

const sizeClasses: Record<Size, string> = {
  sm: "h-8 px-3 text-xs",
  md: "h-10 px-4 text-sm",
  lg: "h-12 px-6 text-base",
};

export const Button = ({
  variant = "primary",
  size = "md",
  loading = false,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) => (
  <button
    {...rest}
    disabled={disabled || loading}
    className={clsx(
      "inline-flex items-center justify-center gap-2 rounded-md font-medium",
      "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
      "disabled:cursor-not-allowed disabled:opacity-60",
      variantClasses[variant],
      sizeClasses[size],
      className,
    )}
  >
    {loading ? (
      <span
        aria-hidden="true"
        className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
      />
    ) : null}
    {children}
  </button>
);
